import { AccountType, AssetSubStatus } from '@src/enums'
import {
    Asset,
    EmissionSignatureDocument,
    TableSorting,
    Task,
} from '@src/types'
import { ApiRes, api } from '@src/utils/api/client'
import { AxiosRequestConfig } from 'axios'

export interface AssetsResponse {
    success: boolean
    assets_count: number
    assets: Asset[]
}

export interface AssetResponse {
    success: boolean
    asset: Asset
}

export interface AssetSignatureDocumentsResponse {
    success: boolean
    documents: EmissionSignatureDocument[]
}

export interface AssetSubStatusResponse {
    success: boolean
    sub_status: AssetSubStatus[]
}

export interface AssetExportResponse {
    message: string
    tasks: Task[]
}

export interface AssetTransferToProjectWalletResponse {
    message: string
    tasks: unknown[]
}

export interface AssetAcceptancePreviewResponse {
    total_paid_amount: number
    order_count: number
    currency: string
}

export interface AssetRunExclusiveAcceptanceResponse {
    log: string
}

export interface AcceptanceRound {
    round_id: number
    order_count: number
    accepted_at: string | null
    closed_at: string | null
    total_amount: number
}

export interface TaskActionResponse {
    message: string
    task: Task
}

interface UploadAssetInvestmentReportResponse {
    message: boolean
    tasks: Task[]
}

export interface SecondaryMarketImportReviewItem {
    row_index: number
    existing_or_new: 'new' | 'exising'
    profile_type: AccountType | null
    profile_name: string | null
    profile_email: string | null
    pre_emptive_rights: number
    is_valid: boolean
    error_reason: string | null
}

export interface PreviewAssetSecondaryMarketImportResponse {
    data: SecondaryMarketImportReviewItem[]
    total_count: number
    invalid_count: number
    upload_key: string
}

interface UploadAssetSecondaryMarketImportResponse {
    message: boolean
    tasks: Task[]
}

export const getAssets = (
    filters?: object,
    page?: number,
    limit?: number,
    sorting?: TableSorting[]
): ApiRes<AssetsResponse> => {
    const config: AxiosRequestConfig = {}

    config.params = {}

    if (filters && Object.keys(filters).length) {
        config.params = filters
    }

    if (typeof page === 'number' && typeof limit === 'number') {
        config.params['range'] = page * limit - limit + '-' + page * limit
    }

    if (sorting && sorting.length) {
        config.params['sort'] = sorting
            .map((sort) => {
                return (sort.direction === 'desc' ? '-' : '') + sort.field
            })
            .join(',')
    }

    return api.get('v2/manage/assets', config)
}

export const createAsset = (asset: Asset): ApiRes<AssetResponse> =>
    api.post('v2/manage/assets', asset)

export const getAsset = (id: string): ApiRes<AssetResponse> =>
    api.get('manage/assets/' + id)

export const deleteAsset = (id: string): ApiRes<string> =>
    api.delete('manage/assets/' + id)

export const deleteAssets = (ids: string[]): ApiRes<string> =>
    api.delete('v2/manage/assets', { data: { assets: ids } })

export const updateAsset = (asset: Asset): ApiRes<AssetResponse> =>
    api.patch('manage/assets/' + asset.asset_uid, asset)

export const exportAssetInvestmentReport = (
    asset: Asset
): ApiRes<AssetExportResponse> =>
    api.post(
        'manage/assets/' + asset.asset_uid + '/export-asset-investment-report',
        { lang: 'en', async: 1 }
    )

export const exportAssetSignatureDocuments = (
    asset: Asset
): ApiRes<AssetExportResponse> =>
    api.post('manage/assets/' + asset.asset_uid + '/export-signed-documents', {
        lang: 'en',
        async: 1,
    })

export const exportCheckOrdersReport = (
    asset: Asset
): ApiRes<AssetExportResponse> =>
    api.post(
        'manage/assets/' + asset.asset_uid + '/export-check-orders-report',
        { lang: 'en', async: 1 }
    )

export const sendAdHocEmailReminder = (
    asset: Asset
): ApiRes<AssetExportResponse> =>
    api.post(
        'manage/assets/' + asset.asset_uid + '/send-no-iban-reminder-email',
        {}
    )

export const sendNyalaRegistryDocumentMail = (
    asset: Asset,
    roundId: number
): ApiRes<{ success: boolean }> =>
    api.post(
        'manage/assets/' +
            asset.asset_uid +
            '/send-nyala-registry-document-email',
        {},
        { params: { round_id: roundId } }
    )

export const retrieveNyalaRegistryExtract = (
    asset: Asset,
    roundId: number
): ApiRes<{ success: boolean }> =>
    api.get(
        'manage/assets/' + asset.asset_uid + '/nyala-registry-extract-retrieve',
        { params: { round_id: roundId } }
    )

export const createNyalaRegistryExtract = (
    asset: Asset
): ApiRes<{ log: string }> =>
    api.post(
        'manage/assets/' + asset.asset_uid + '/nyala-registry-extract-create',
        {}
    )

export const uploadAssetInvestmentReport = (
    asset: Asset,
    uploadKey: string
): ApiRes<UploadAssetInvestmentReportResponse> =>
    api.post(
        'manage/assets/' + asset.asset_uid + '/asset-investment-report-upload',
        {
            upload_key: uploadKey,
        }
    )

export const previewAssetSecondaryMarketImport = (
    asset: Asset,
    uploadKey: string
): ApiRes<PreviewAssetSecondaryMarketImportResponse> =>
    api.post(
        'v2/manage/assets/' +
            asset.asset_uid +
            '/asset-secondary-market-import-preview',
        {
            upload_key: uploadKey,
        }
    )

export const uploadAssetSecondaryMarketImport = (
    asset: Asset,
    uploadKey: string
): ApiRes<UploadAssetSecondaryMarketImportResponse> =>
    api.post(
        'v2/manage/assets/' +
            asset.asset_uid +
            '/asset-secondary-market-import-upload',
        {
            upload_key: uploadKey,
        }
    )

export const getAcceptancePreview = (
    asset: Asset,
    ordersCreatedUntil?: string
): ApiRes<AssetAcceptancePreviewResponse> =>
    api.get('manage/assets/' + asset.asset_uid + '/acceptance-preview', {
        params: { orders_created_until: ordersCreatedUntil },
    })

export const transferToProjectWallet = (
    asset: Asset,
    wallet: string,
    date: string
): ApiRes<AssetTransferToProjectWalletResponse> =>
    api.post('manage/assets/' + asset.asset_uid + '/acceptance', {
        external_wallet_id: wallet,
        orders_created_until: date,
    })

export const runExclusiveAcceptance = (
    asset: Asset,
    wallet: string
): ApiRes<AssetRunExclusiveAcceptanceResponse> =>
    api.post('manage/assets/' + asset.asset_uid + '/exclusive-acceptance', {
        external_wallet_id: wallet,
    })

export const runBulkSetPendingOrders = (asset: Asset): ApiRes<unknown> =>
    api.post('manage/assets/' + asset.asset_uid + '/bulk-orders-set-pending', {
        async: 1,
    })

export const runBulkSetPaidOrders = (asset: Asset): ApiRes<unknown> =>
    api.post('manage/assets/' + asset.asset_uid + '/bulk-orders-set-paid', {
        async: 1,
    })

export const transferTokens = (
    asset: Asset,
    roundId?: number
): ApiRes<AssetTransferToProjectWalletResponse> =>
    api.post(
        'manage/assets/' + asset.asset_uid + '/transfer-tokens',
        {},
        { params: roundId ? { round_id: roundId } : undefined }
    )

export const getAcceptanceRounds = (asset: Asset): ApiRes<AcceptanceRound[]> =>
    api.get('manage/assets/' + asset.asset_uid + '/acceptance-rounds')

export const closeAcceptanceRound = (
    asset: Asset,
    roundId: number
): ApiRes<{ message: string }> =>
    api.post(
        'manage/assets/' +
            asset.asset_uid +
            '/acceptance-rounds/' +
            roundId +
            '/close'
    )

export const updateStakeholderAmount = (
    asset: Asset,
    roundId: number,
    stakeholderFee: number
): ApiRes<TaskActionResponse> =>
    api.post(
        'manage/assets/' + asset.asset_uid + '/update-stakeholder-amount',
        {
            round_id: roundId,
            stakeholder_fee: stakeholderFee,
        }
    )

export const releaseFunds = (
    asset: Asset,
    roundId: number
): ApiRes<TaskActionResponse> =>
    api.post('manage/assets/' + asset.asset_uid + '/release-funds', {
        round_id: roundId,
    })

export const sendOptIns = (
    asset: Asset,
    roundId: number
): ApiRes<TaskActionResponse> =>
    api.post('manage/assets/' + asset.asset_uid + '/send-opt-ins', {
        round_id: roundId,
    })

export const getAssetSubStatus = (
    status: string
): ApiRes<AssetSubStatusResponse> =>
    api.get(`v2/manage/assets/get-sub-status?status=${status}`)

export const getAssetSignatureDocuments = (
    asset_uid: string
): ApiRes<AssetSignatureDocumentsResponse> =>
    api.get('manage/assets/' + asset_uid + '/signature-documents')

export const uploadAssetSignatureDocument = (
    asset: Asset,
    signatureDocument: EmissionSignatureDocument
): ApiRes<AssetSignatureDocumentsResponse> =>
    api.post(
        'manage/assets/' + asset.asset_uid + '/signature-documents',
        signatureDocument
    )

export const bulkDeleteAssetSignatureDocuments = (
    asset: Asset,
    signatureDocuments: string[]
): ApiRes<AssetSignatureDocumentsResponse> =>
    api.delete('manage/assets/' + asset.asset_uid + '/signature-documents', {
        data: {
            signature_documents: signatureDocuments,
        },
    })

export const updateAssetSignatureDocument = (
    asset: Asset,
    signatureDocument: EmissionSignatureDocument
): ApiRes<AssetSignatureDocumentsResponse> =>
    api.put(
        'manage/assets/' + asset.asset_uid + '/signature-documents',
        signatureDocument
    )
